import type {
  Theme,
  Components,
  ComponentsVariants,
} from '@mui/material/styles';

import { switchClasses } from '@mui/material/Switch';
import { formControlLabelClasses } from '@mui/material/FormControlLabel';

import { themeConfig } from '../../theme-config';

// ----------------------------------------------------------------------

type SwitchVariants = ComponentsVariants<Theme>['MuiSwitch'];

const DIMENSIONS: Record<
  'mobile' | 'desktop',
  {
    root: { width: number; height: number };
    thumb: number;
    trackWidth: number;
    trackHeight: number;
    trackRadius: number;
    translateX: string;
  }
> = {
  mobile: {
    root: { width: 34, height: 20 },
    thumb: 14,
    trackWidth: 34,
    trackHeight: 20,
    trackRadius: 10,
    translateX: '14px',
  },
  desktop: {
    root: { width: 26, height: 16 },
    thumb: 10,
    trackWidth: 26,
    trackHeight: 16,
    trackRadius: 8,
    translateX: '10px',
  },
};

/* **********************************************************************
 * 🗳️ Variants
 * **********************************************************************/
const colorVariants = [
  {
    props: (props) => props.color === 'default',
    style: ({ theme }) => ({
      [`&.${switchClasses.checked}`]: {
        [`& + .${switchClasses.track}`]: {
          backgroundColor: theme.vars.palette.text.primary,
        },
        [`& .${switchClasses.thumb}`]: {
          ...theme.applyStyles('dark', {
            color: theme.vars.palette.grey[900],
          }),
        },
      },
    }),
  },
] satisfies SwitchVariants;

const disabledVariants = [
  {
    props: {},
    style: ({ theme }) => ({
      [`&.${switchClasses.disabled}`]: {
        [`& + .${switchClasses.track}`]: {
          opacity: theme.vars.opacity.switchTrackDisabled,
        },
        [`& .${switchClasses.thumb}`]: {
          ...theme.applyStyles('dark', {
            opacity: theme.vars.opacity.switchTrackDisabled,
          }),
        },
      },
    }),
  },
] satisfies SwitchVariants;

/* **********************************************************************
 * 🧩 Components
 * **********************************************************************/
const MuiSwitch: Components<Theme>['MuiSwitch'] = {
  // ▼▼▼▼▼▼▼▼ 🎨 STYLE ▼▼▼▼▼▼▼▼
  styleOverrides: {
    root: ({ theme }) => ({
      alignItems: 'center',
      // MUI's stock root has a 12px padding on every side, sized for its own
      // (much larger) stock width/height. Since we size root to hug the
      // track exactly (no dead space), that padding must be zeroed out too
      // — otherwise the content-box shrinks below track's own dimensions
      // and the flex layout pushes track outside root's bounds.
      padding: 0,
      // flexShrink/flexGrow/minWidth/minHeight: root itself is often a flex
      // child of whatever wraps it (FormControlLabel, a settings row, etc.)
      // — without these, the browser's default flex-shrink behavior can
      // silently distort root's explicit size at the `sm` breakpoint below,
      // the same class of bug the track style below guards against.
      flexShrink: 0,
      flexGrow: 0,
      minWidth: 0,
      minHeight: 0,
      width: DIMENSIONS.mobile.root.width,
      height: DIMENSIONS.mobile.root.height,
      [theme.breakpoints.up('sm')]: {
        width: DIMENSIONS.desktop.root.width,
        height: DIMENSIONS.desktop.root.height,
      },
      // FormControlLabel is built around MUI's stock Switch padding, which the
      // explicit sizing above zeroes out. Both of its compensations then land
      // on a control that no longer has the padding they were offsetting:
      //
      //  - marginLeft: -11px pulled the control 11px left, to sit its *visual*
      //    edge flush with the row. With padding gone that is the track's own
      //    edge, so the track hangs 11px outside the row and any ancestor with
      //    `overflow: hidden` (e.g. Ari chat's Settings > Memory panel) slices
      //    it off — the switch renders as a cut-off square. Cancel it.
      //  - the right-hand gap to the label came from the same padding, so
      //    restore that as margin. Margin, not padding, keeps root's box — and
      //    so the hit area — hugging the visible control.
      //
      // Default labelPlacement only; `start`/`top`/`bottom` mirror these
      // margins and no consumer in this codebase uses them.
      [`.${formControlLabelClasses.root} > &`]: {
        marginLeft: '11px',
        marginRight: theme.spacing(1),
      },
      // MUI's `size="small"` variant reaches the thumb and switchBase through
      // DESCENDANT selectors on root (`& .MuiSwitch-thumb`), which outrank the
      // single-class slot overrides below no matter what order they land in.
      // A small Switch therefore kept MUI's stock 16px thumb, 4px switchBase
      // padding and 16px travel inside our 26px track — overflowing it and
      // clipping against root's bounds. Re-assert our values at that same
      // specificity. Size here is a function of the breakpoint, not the `size`
      // prop, so small and medium are deliberately identical.
      [`&.${switchClasses.sizeSmall}`]: {
        [`& .${switchClasses.thumb}`]: {
          width: DIMENSIONS.mobile.thumb,
          height: DIMENSIONS.mobile.thumb,
          [theme.breakpoints.up('sm')]: {
            width: DIMENSIONS.desktop.thumb,
            height: DIMENSIONS.desktop.thumb,
          },
        },
        [`& .${switchClasses.switchBase}`]: {
          padding: 0,
          [`&.${switchClasses.checked}`]: {
            transform: 'translateX(var(--switch-thumb-offset, 0px))',
          },
        },
      },
    }),
    switchBase: ({ theme }) => ({
      top: 'auto',
      // switchBase is sized to the track's height, so the thumb is already
      // centred within it — that centring alone is the 3px horizontal inset,
      // matching the vertical one. An extra `left: 3px` stacked on top of it,
      // starting the thumb at 6px and landing it flush (0px) against the
      // track's right edge after the full travel. Anchor at 0 so both ends
      // inset by 3px; translateX below is already the correct distance.
      left: 0,
      // MUI's stock switchBase is a fixed 38x38 (padded well beyond the
      // thumb) — far bigger than our compact root now that root hugs track
      // exactly, so it must be pinned to the thumb's own footprint (padding
      // zeroed too) or it overflows root's bounds (clipped via
      // overflow:hidden).
      padding: 0,
      width: DIMENSIONS.mobile.trackHeight,
      height: DIMENSIONS.mobile.trackHeight,
      // The checked-state slide reads its distance from a CSS variable rather
      // than declaring `transform` twice (once unconditional, once under the
      // `sm` media query): the CSS-in-JS pipeline here silently drops one of
      // the two `transform` declarations when the same compiled selector
      // ends up with two rules for it at asymmetric nesting depth — verified
      // directly against the generated stylesheet, not just inferred from
      // behavior. width/height above don't hit this (different property).
      // A custom property has no such special-casing, so each breakpoint
      // just overrides the variable's value like any other declaration.
      transform: 'translateX(var(--switch-thumb-offset, 0px))',
      // Slide the thumb instead of snapping it (state change should animate;
      // transform-only, off the main thread). Honors reduced-motion.
      transition: 'transform 180ms cubic-bezier(0.32, 0.72, 0, 1)',
      '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      '&:focus-visible': {
        [`& + .${switchClasses.track}`]: {
          outline: 'none',
          boxShadow: `0 0 0 3px ${theme.vars.palette.grey[300]}`,
        },
      },
      [`&.${switchClasses.checked} + .${switchClasses.track}`]: {
        background: themeConfig.gradients.primary.colors[3],
        opacity: 1,
      },
      [`&.${switchClasses.checked}`]: {
        // MUI's own stock `.checked` rule sets a literal `transform` at this
        // same specificity; redeclaring it here (identical expression, only
        // the variable's value differs per breakpoint) is what lets our
        // override win instead of the unconditional one above losing to it.
        transform: 'translateX(var(--switch-thumb-offset, 0px))',
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
      // The two breakpoints that set `--switch-thumb-offset` are kept at
      // identical nesting depth (each its own top-level media key wrapping
      // `.checked`, mutually exclusive down/up) rather than one being
      // unconditional and the other nested inside it — an asymmetric
      // nesting of the SAME property name inside the SAME parent selector
      // caused the shallower declaration to be silently dropped from the
      // compiled CSS entirely (observed directly via the stylesheet, not
      // just a cascade/specificity issue).
      [theme.breakpoints.down('sm')]: {
        [`&.${switchClasses.checked}`]: {
          '--switch-thumb-offset': DIMENSIONS.mobile.translateX,
        },
      },
      [theme.breakpoints.up('sm')]: {
        width: DIMENSIONS.desktop.trackHeight,
        height: DIMENSIONS.desktop.trackHeight,
        [`&.${switchClasses.checked}`]: {
          '--switch-thumb-offset': DIMENSIONS.desktop.translateX,
        },
      },
      variants: [...colorVariants, ...disabledVariants],
    }),
    thumb: ({ theme }) => ({
      width: DIMENSIONS.mobile.thumb,
      height: DIMENSIONS.mobile.thumb,
      color: theme.vars.palette.common.white,
      boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
      [theme.breakpoints.up('sm')]: {
        width: DIMENSIONS.desktop.thumb,
        height: DIMENSIONS.desktop.thumb,
      },
    }),
    track: ({ theme }) => ({
      // flexShrink/flexGrow/minWidth: root is a flex container and track is
      // its only flex-participating child (switchBase is absolutely
      // positioned, so it's out of flow) — without these, the browser's
      // default flex-shrink behavior silently distorts track's explicit
      // width at the `sm` breakpoint below.
      flexShrink: 0,
      flexGrow: 0,
      minWidth: 0,
      width: DIMENSIONS.mobile.trackWidth,
      height: DIMENSIONS.mobile.trackHeight,
      borderRadius: DIMENSIONS.mobile.trackRadius,
      backgroundColor: theme.vars.palette.grey[500],
      [theme.breakpoints.up('sm')]: {
        width: DIMENSIONS.desktop.trackWidth,
        height: DIMENSIONS.desktop.trackHeight,
        borderRadius: DIMENSIONS.desktop.trackRadius,
      },
    }),
  },
};

/* **********************************************************************
 * 🚀 Export
 * **********************************************************************/
export const switches: Components<Theme> = {
  MuiSwitch,
};
